import React, { useState, useEffect } from "react";
import { Zap, RefreshCw, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { DataTable, platformBadge, PLATFORM_FILTER } from "./shared";

const statusBadge = (s) => {
  const map = {
    reserved: ["bg-blue-100 text-blue-700", "Reservasi (stok keluar)"],
    ordered: ["bg-emerald-100 text-emerald-700", "Pesanan customer"],
    returned: ["bg-slate-200 text-slate-700", "Ditarik balik"],
    returned_but_ordered: ["bg-amber-100 text-amber-700", "Ditarik tapi sudah dipesan"],
    await_reservation: ["bg-amber-100 text-amber-700", "Menunggu reservasi"],
    skipped: ["bg-slate-200 text-slate-700", "Skip"],
    failed: ["bg-red-100 text-red-700", "Gagal"],
  };
  const [cls, label] = map[s] || ["bg-slate-200 text-slate-700", s || "-"];
  return <Badge className={`${cls} hover:${cls}`}>{label}</Badge>;
};

const cols = [
  { key: "booking_sn", header: "ID Reservasi", className: "font-mono text-xs", sortAccessor: (r) => r.booking_sn, render: (r) => r.booking_sn || "-" },
  { key: "order_sn", header: "ID Pesanan", className: "font-mono text-xs", render: (r) => r.order_sn || <span className="text-cocoa/40">—</span> },
  { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
  { key: "status", header: "Status", render: (r) => statusBadge(r.status) },
  { key: "sale_number", header: "No Sales", className: "text-sm", render: (r) => r.sale_number || "-" },
  { key: "keep_number", header: "No Keep", className: "text-sm", render: (r) => r.keep_number || "-" },
  { key: "reason", header: "Catatan", className: "text-xs text-cocoa/70 max-w-xs", render: (r) => r.reason || "-" },
];
const statusFilter = { key: "status", label: "Status", allLabel: "Semua status", match: (r, v) => r.status === v,
  options: [
    { value: "reserved", label: "Reservasi" }, { value: "ordered", label: "Pesanan customer" },
    { value: "returned", label: "Ditarik balik" }, { value: "await_reservation", label: "Menunggu reservasi" },
    { value: "failed", label: "Gagal" }, { value: "skipped", label: "Skip" },
  ] };

export default function KilatView({ write }) {
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/desty/kilat-log").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => {
    api.get("/desty/autokeep-config").then((r) => setCfg(r.data)).catch(() => {});
    load();
  }, []);

  const patchCfg = async (next, okMsg) => {
    const prev = cfg;
    setCfg(next); setSaving(true);
    try { await api.put("/desty/autokeep-config", next); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCfg(prev); }
    finally { setSaving(false); }
  };
  const toggleMaster = (v) => patchCfg({ ...cfg, kilat_enabled: v }, v ? "Auto Kilat diaktifkan (jalan tiap 15 menit)" : "Auto Kilat dimatikan");
  const togglePlatform = (p, v) => patchCfg({ ...cfg, [p]: { ...(cfg[p] || {}), kilat: v } });

  const runNow = async () => {
    setRunning(true);
    try {
      const s = (await api.post("/desty/kilat-run")).data || {};
      if (s.ran === false) toast.error(s.reason || "Auto Kilat nonaktif — aktifkan dulu");
      else if (s.error) toast.error("Gagal proses kilat: " + s.error);
      else toast.success(`Selesai: ${s.reserved || 0} reservasi, ${s.ordered || 0} pesanan, ${s.returned || 0} ditarik balik, ${s.failed || 0} gagal`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const byStatus = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div className="space-y-4" data-testid="kilat-view">
      <div className="flex items-start justify-between flex-wrap gap-3 rounded-lg border border-cocoa/10 bg-white p-4">
        <div className="max-w-2xl">
          <div className="font-semibold text-cocoa flex items-center gap-2"><Zap className="w-4 h-4" /> Auto Kilat — Pesanan Kilat Shopee (FBS)</div>
          <p className="text-xs text-cocoa/60 mt-1">
            Saat stok dikirim ke Gudang Shopee (ID Reservasi), sistem otomatis buat Keep + Sales (stok keluar). Saat ada pesanan customer
            (ID Pesanan), Sales-nya diperbarui dengan ID Pesanan — ID Reservasi & ID Pesanan sama-sama tersimpan. Jika reservasi ditarik balik,
            Sales dibatalkan & stok dikembalikan. Group/Customer/Gudang mengikuti pengaturan di tab <b>Auto Keep</b>. Harga = selling price ERP.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {write && <div className="flex items-center gap-2 text-sm"><span className="text-cocoa/60">Aktif</span>
            <Switch checked={!!cfg?.kilat_enabled} disabled={saving || !cfg} onCheckedChange={toggleMaster} data-testid="kilat-master-toggle" /></div>}
          {write && <Button variant="outline" onClick={runNow} disabled={running} data-testid="kilat-run-now">
            <Play className={`w-4 h-4 mr-1.5 ${running ? "animate-pulse" : ""}`} /> {running ? "Memproses…" : "Jalankan Sekarang"}</Button>}
        </div>
      </div>

      {write && cfg && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-cocoa/10 bg-white p-4 text-sm">
          <span className="text-cocoa/60">Aktifkan per platform:</span>
          <div className="flex items-center gap-2"><span className="capitalize">Shopee</span>
            <Switch checked={!!cfg.shopee?.kilat} onCheckedChange={(v) => togglePlatform("shopee", v)} data-testid="kilat-toggle-shopee" /></div>
          <div className="flex items-center gap-2"><span className="capitalize">Tiktok</span>
            <Switch checked={!!cfg.tiktok?.kilat} onCheckedChange={(v) => togglePlatform("tiktok", v)} data-testid="kilat-toggle-tiktok" /></div>
          <span className="text-[11px] text-cocoa/40">Pesanan Kilat umumnya hanya di Shopee.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs" data-testid="kilat-summary">
        <span className="rounded-full bg-blue-100 text-blue-700 px-2.5 py-1">Reservasi: <b>{byStatus("reserved")}</b></span>
        <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1">Pesanan customer: <b>{byStatus("ordered")}</b></span>
        <span className="rounded-full bg-slate-200 text-slate-700 px-2.5 py-1">Ditarik balik: <b>{byStatus("returned")}</b></span>
        <span className="rounded-full bg-red-100 text-red-700 px-2.5 py-1">Gagal: <b>{byStatus("failed")}</b></span>
      </div>

      <div className="flex items-center justify-end">
        <Button variant="ghost" size="sm" onClick={load} data-testid="kilat-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <DataTable testid="kilat-log-table" columns={cols} rows={rows}
        searchAccessors={[(r) => r.booking_sn, (r) => r.order_sn, (r) => r.sale_number, (r) => r.reason]}
        searchPlaceholder="Cari ID Reservasi / ID Pesanan / no sales…"
        filters={[PLATFORM_FILTER, statusFilter]}
        rowTestId={(r) => `kilat-row-${r.booking_sn}`}
        emptyText="Belum ada data Pesanan Kilat. Aktifkan lalu klik 'Jalankan Sekarang'." />
    </div>
  );
}
